import React, { useState, useEffect } from 'react';
import {
  Modal,
  ModalDialog,
  ModalClose,
  Typography,
  FormControl,
  FormLabel,
  Input,
  Select,
  Option,
  Button,
  Stack,
  Checkbox,
  Alert,
} from '@mui/joy';
import { priceListApi, distributorsApi, type PriceListItem, type Distributor } from '../../lib/distributorsApi';
import { useNotification } from '../../contexts/NotificationContext';

interface Props {
  open: boolean;
  onClose: () => void;
  product: PriceListItem | null;
  onSave: () => void;
}

// Product categories from backend
const PRODUCT_CATEGORIES = [
  { value: 'shingles', label: 'Shingles' },
  { value: 'underlayment', label: 'Underlayment' },
  { value: 'ridge_vents', label: 'Ridge Vents' },
  { value: 'flashing', label: 'Flashing' },
  { value: 'fasteners', label: 'Fasteners' },
  { value: 'adhesives', label: 'Adhesives' },
  { value: 'gutters', label: 'Gutters' },
  { value: 'insulation', label: 'Insulation' },
  { value: 'ventilation', label: 'Ventilation' },
  { value: 'tools', label: 'Tools' },
  { value: 'safety', label: 'Safety Equipment' },
  { value: 'misc', label: 'Miscellaneous' },
];

// Units of measure from backend
const UNITS_OF_MEASURE = [
  { value: 'EA', label: 'Each' },
  { value: 'BD', label: 'Bundle' },
  { value: 'SQ', label: 'Square' },
  { value: 'LF', label: 'Linear Foot' },
  { value: 'SF', label: 'Square Foot' },
  { value: 'GAL', label: 'Gallon' },
  { value: 'LB', label: 'Pound' },
  { value: 'BX', label: 'Box' },
  { value: 'CT', label: 'Carton' },
  { value: 'CS', label: 'Case' },
  { value: 'RL', label: 'Roll' },
  { value: 'PC', label: 'Piece' },
  { value: 'PK', label: 'Pack' },
  { value: 'TB', label: 'Tube' },
];

export default function ProductForm({ open, onClose, product, onSave }: Props) {
  const { showSuccess, showError } = useNotification();
  const [loading, setLoading] = useState(false);
  const [distributors, setDistributors] = useState<Distributor[]>([]);
  const [error, setError] = useState<string | null>(null);
  
  // Form fields
  const [formData, setFormData] = useState({
    distributor: product?.distributor || '',
    product_name: product?.product_name || '',
    product_id: product?.product_id || '',
    category: product?.category || 'misc',
    unit_price: product?.unit_price || '',
    unit_of_measure: product?.unit_of_measure || 'EA',
    is_active: product?.is_active !== undefined ? product.is_active : true,
  });

  useEffect(() => {
    if (open) {
      loadDistributors();
      // Reset form data when opening with different product
      if (product) {
        setFormData({
          distributor: product.distributor,
          product_name: product.product_name,
          product_id: product.product_id || '',
          category: product.category,
          unit_price: product.unit_price,
          unit_of_measure: product.unit_of_measure,
          is_active: product.is_active,
        });
      } else {
        // Reset for new product
        setFormData({
          distributor: '',
          product_name: '',
          product_id: '',
          category: 'misc',
          unit_price: '',
          unit_of_measure: 'EA',
          is_active: true,
        });
      }
    }
  }, [open, product]);

  const loadDistributors = async () => {
    try {
      const response = await distributorsApi.list({ is_active: true });
      setDistributors(response?.results || []);
    } catch (err: any) {
      setError('Failed to load distributors');
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    
    // Validation
    if (!formData.distributor) {
      setError('Please select a distributor');
      return;
    }
    if (!formData.product_name.trim()) {
      setError('Product name is required');
      return;
    }
    if (!formData.unit_price || parseFloat(formData.unit_price.toString()) <= 0) {
      setError('Please enter a valid price');
      return;
    }

    setLoading(true);
    try {
      const data = {
        ...formData,
        distributor: parseInt(formData.distributor.toString()),
        unit_price: formData.unit_price.toString(),
      };

      if (product) {
        // Update existing product
        await priceListApi.update(product.id, data);
        showSuccess('Product updated successfully');
      } else {
        // Create new product
        await priceListApi.create(data);
        showSuccess('Product created successfully');
      }
      
      onSave();
      handleClose();
    } catch (err: any) {
      showError(err.response?.data?.detail || 'Failed to save product');
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setError(null);
    setFormData({
      distributor: '',
      product_name: '',
      product_id: '',
      category: 'misc',
      unit_price: '',
      unit_of_measure: 'EA',
      is_active: true,
    });
    onClose();
  };

  return (
    <Modal open={open} onClose={handleClose}>
      <ModalDialog sx={{ width: '90%', maxWidth: 500 }}>
        <ModalClose />
        
        <Typography level="h3" sx={{ mb: 2 }}>
          {product ? 'Edit Product' : 'Add Product'}
        </Typography>

        <form onSubmit={handleSubmit}>
          <Stack spacing={2}>
            {error && (
              <Alert color="danger">
                {error}
              </Alert>
            )}

            <FormControl required>
              <FormLabel>Distributor</FormLabel>
              <Select
                value={formData.distributor.toString()}
                onChange={(_, value) => setFormData({ ...formData, distributor: value as string })}
                disabled={!!product} // Can't change distributor when editing
              >
                <Option value="">Select a distributor</Option>
                {distributors.map(dist => (
                  <Option key={dist.id} value={dist.id.toString()}>
                    {dist.name}
                  </Option>
                ))}
              </Select>
            </FormControl>

            <FormControl required>
              <FormLabel>Product Name</FormLabel>
              <Input
                value={formData.product_name}
                onChange={(e) => setFormData({ ...formData, product_name: e.target.value })}
                placeholder="Enter product name"
              />
            </FormControl>

            <FormControl>
              <FormLabel>Product ID/SKU</FormLabel>
              <Input
                value={formData.product_id}
                onChange={(e) => setFormData({ ...formData, product_id: e.target.value })}
                placeholder="Optional product ID or SKU"
              />
            </FormControl>

            <Stack direction="row" spacing={2}>
              <FormControl required sx={{ flex: 1 }}>
                <FormLabel>Category</FormLabel>
                <Select
                  value={formData.category}
                  onChange={(_, value) => setFormData({ ...formData, category: value as string })}
                >
                  {PRODUCT_CATEGORIES.map(cat => (
                    <Option key={cat.value} value={cat.value}>
                      {cat.label}
                    </Option>
                  ))}
                </Select>
              </FormControl>

              <FormControl required sx={{ flex: 1 }}>
                <FormLabel>Unit of Measure</FormLabel>
                <Select
                  value={formData.unit_of_measure}
                  onChange={(_, value) => setFormData({ ...formData, unit_of_measure: value as string })}
                >
                  {UNITS_OF_MEASURE.map(unit => (
                    <Option key={unit.value} value={unit.value}>
                      {unit.label}
                    </Option>
                  ))}
                </Select>
              </FormControl>
            </Stack>

            <FormControl required>
              <FormLabel>Unit Price ($)</FormLabel>
              <Input
                type="number"
                value={formData.unit_price}
                onChange={(e) => setFormData({ ...formData, unit_price: e.target.value })}
                placeholder="0.00"
                slotProps={{
                  input: {
                    min: 0,
                    step: 0.01,
                  },
                }}
              />
            </FormControl>

            <FormControl>
              <Checkbox
                label="Active (Product is available for ordering)"
                checked={formData.is_active}
                onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
              />
            </FormControl>

            <Stack direction="row" spacing={2} sx={{ mt: 3 }}>
              <Button
                type="submit"
                color="primary"
                loading={loading}
                sx={{ flex: 1 }}
              >
                {product ? 'Update Product' : 'Create Product'}
              </Button>
              <Button
                variant="outlined"
                onClick={handleClose}
                disabled={loading}
                sx={{ flex: 1 }}
              >
                Cancel
              </Button>
            </Stack>
          </Stack>
        </form>
      </ModalDialog>
    </Modal>
  );
}